import bindAllMethods from '../../../utils/bindAllMethods';
import {
  GetProviderListParam,
  GetProviderListResponseType,
  IProviderClient,
  ProviderOrigin
} from '../../../clients/shell/provider';
import { GenerateAuthenticationUrlParams } from './types';
import { SetServiceDependencies } from '../../../infra/startup/types';
import { StateParamHandlerService } from '../../../services/stateParamHandlerService';
import {
  AuthorizationUrlBuilder,
  IAuthorizationUrlBuilder
} from './AuthorizationUrlBuilder';
import BaseLoginService from './BaseLoginService';
import ILoginService from './ILoginService';
import { AuthPluginFacade } from '../../JWeb/facades/AuthPluginFacade';

class LoginServiceNative extends BaseLoginService implements ILoginService {
  private _stateParamHandlerService!: StateParamHandlerService;

  public setDependencies({
    clients,
    services,
    ...rest
  }: SetServiceDependencies): void {
    super.setDependencies({ clients, services, ...rest });
    const { shell } = clients;
    this._stateParamHandlerService = services.stateParamHandlerService;
    bindAllMethods(this);
  }

  public async getProviderList(
    options?: GetProviderListParam
  ): Promise<GetProviderListResponseType> {
    try {
      const response = await AuthPluginFacade.getIdentityProviderConfigurations(
        {
          email: options?.email || ''
        }
      );

      const error = (response as any)?.error;
      if (error) {
        throw new Error(
          error.reason || error.code || 'IDP configuration error'
        );
      }

      const configs = response?.idpConfigurations || [];

      return configs
        .map((config) => {
          const name = config?.idpObject?.name;
          const authorizationEndpoint =
            config?.idpServiceDescription?.links?.find(
              (link) => link?.rel === 'auth start endpoint'
            )?.href || '';

          if (!name || !authorizationEndpoint) {
            return null;
          }

          return {
            name,
            authorizationEndpoint,
            origin: this._mapOrigin(config.origin)
          };
        })
        .filter(Boolean) as GetProviderListResponseType;
    } catch (error: any) {
      throw new Error(
        error?.reason ||
          error?.code ||
          error?.message ||
          'IDP configuration error'
      );
    }
  }

  private _mapOrigin(origin?: string[]): ProviderOrigin[] {
    if (!origin?.length) return [];

    return origin.filter(
      (value): value is ProviderOrigin =>
        value === ProviderOrigin.email || value === ProviderOrigin.domain
    );
  }

  // TODO: Check this. It doesn't make sense to return a single entry if we support multiple authenticationURls
  public async generateAuthenticationUrl(
    options: GenerateAuthenticationUrlParams
  ): Promise<string> {
    const postLoginRedirect = options.postLoginRedirect || '';
    const language = options.language || '';
    const country = options.country || '';

    const authorizationUrlBuilder: IAuthorizationUrlBuilder =
      new AuthorizationUrlBuilder(
        options.authorizationUrl,
        this._stateParamHandlerService
      );

    authorizationUrlBuilder
      .withMaxAgeZeroOnLogin()
      .withAuthorizationExtraParams()
      .withState(postLoginRedirect, language, country)
      .withLocale(language, country);

    return authorizationUrlBuilder.build();
  }
}

export default LoginServiceNative;
